import user_model from 'models/user_model';
import prods_model from 'models/prods_model';
import currency from 'currency.js';
import moment from "moment";
import firebase_database from '../../xAppLib/providers/firebase_database';
import discount_engine from '../../models/discount_engine';
import { getPriceCoeff, getSpecPrices } from '../admin/prods/priceUtils';
export { getPriceCoeff, getSpecPrices };

const SPECIAL_OFFERS_PID = 'dstBqZs';
export const DEFAULT_DELIVERY_PRICE = 15;
export const SPEC_PRICES = (pid) => (getSpecPrices())[pid];
export const PRICE_COEFF = () => getPriceCoeff();

export const IS_FREEBIE = (prod) => !!prod.conf?.is_freebie;
export const CART_LOC = () =>`cosm_carts/${app.user?.prof?.uid}`;
export const SHOP_ACCESS = () => user_model.limit_access('buy_prods', 'cosm_prod', 'view_trans');
export const CAN_BUY = () => user_model.check_access('buy_prods', 'cosm_prod');
export const IS_PRODUCT = (prod) => prod.active && prod.price != null && prod.supp_oid && !IS_FREEBIE(prod);
export const COSM_PRODUCTS = (prods) => prods.filter(IS_PRODUCT);
export const COSM_CAT_PRODS = (prods, par_pid) => prods.filter?.((prod) => IS_PRODUCT(prod) && prod.par_pid === par_pid);
export const COSM_CATEGORY = (prods, par_pid) => prods.find?.((prod) => !IS_PRODUCT(prod) && prod.pid === par_pid);
export const COSM_CATEGORIES = (prods) => prods.filter?.((prod) => !IS_PRODUCT(prod) && !IS_FREEBIE(prod) && ![prod.pid, prod.par_pid].includes(SPECIAL_OFFERS_PID));
export const COSM_SPECIAL_OFFERS = (prods) => prods.filter?.((prod) => prod.active && prod.par_pid === SPECIAL_OFFERS_PID);
export const CART_PRODS = (prods, cart) => prods.filter?.((prod) => IS_PRODUCT(prod) && cart?.qty?.[prod.pid]);
export const COSM_TAGS = (prods) => prods.reduce((acc, prod) => {
  prod.conf?.tags?.forEach?.((tag) => acc[tag] = [...(acc[tag] || []), prod]);
  return acc;
}, {});

export const FREEBIES = (cartProds, qty) => {
	// Get all cosmetic products from the global app data
	const prods = app.app_data.ws_data?.cosm_prods;
	
	// Extract freebie configurations from cart products and organize by parent product ID
	const freebiesConfs = cartProds.reduce((acc, cartProd) => {
		// Expected freebieConf type: { prods: string[] , eligible_prods: string[], tiers: { amount: number, free: number }[], multiple: boolean }[]
		// Find the parent product and get its freebie configuration (array of freebie rules)
		const freebieConf = prods.find((prod) => prod.pid === cartProd.par_pid).conf?.freebies || [];
		// Add this configuration to accumulator if it exists, keyed by parent product ID
		return { ...acc, ...(freebieConf.length && { [cartProd.par_pid]: freebieConf })};
	}, {});

	// Process each product's freebie configurations to calculate eligible freebies
	return Object.entries(freebiesConfs).reduce((acc, [pid, confs]) => {
		// Process each individual freebie configuration for this product
		const freebies = confs.reduce((result, conf, confIndex) => {
			// Calculate total quantity of qualifying products by summing cart quantities for products in conf.prods
			const amount = conf.prods?.reduce((sum, prod) => (qty?.[prod] || 0) + sum, 0);

			// Find the best applicable tier by checking the amount against each tier
			const bestTier = conf.tiers?.reduce?.((best, tier) => {
				// Check if customer has enough quantity AND this tier is better than current best
				if (amount >= tier.amount && tier.amount > (best?.amount || 0)) {
					return tier; // Select this tier as the new best
				}
				return best; // Keep the current best tier
			}, null);

			// Only proceed if customer qualifies for a tier
			if (bestTier) {
				// Helper function to find the highest tier threshold amount
				const highestTierAmount = () => conf.tiers.reduce((best, tier) => Math.max(best, tier.amount), 0);

				// Calculate how many free items customer gets based on multiple flag and tier rules
				const freeAmount = conf.multiple && amount > highestTierAmount()
					? Math.floor(amount / bestTier.amount) * bestTier.free  // Multiple tiers: calculate based on quantity multiples
					: bestTier.free; // Single tier: just give the tier's free amount

				// Add a title based on the tier, e.g. "Buy 10 get 5 freebies"
				const title = `Buy ${bestTier.amount} get ${bestTier.free} freebie${bestTier.free > 1 ? 's' : ''}`;

				// Add this freebie offer to results with eligible products, amount, unique ID, and title
				result = [...result, { prods: prods.filter((prod) => conf.eligible_prods?.includes(prod.pid)), freeAmount, fid: `${confIndex}:${pid}`, title }];
			}

			// Return accumulated results for this configuration
			return result;
		}, []);

		// Add all freebies from this product to the main accumulator
		return [...acc, ...freebies];
	}, []);
};



export function getUpdatedFreebies (cartProds, qty, cartFreebies = {}) {
	if (!cartProds?.length) return;

	const freebieKeys = Object.keys(cartFreebies);
	if (freebieKeys.length) {
		const newFreebies = FREEBIES(cartProds, qty);

		if (freebieKeys.length > newFreebies.length) {
			const missingParFreebie = freebieKeys.find((f) => !newFreebies.some((nf) => nf.fid === f));
			const { [missingParFreebie]: _omitted, ...updatedFreebies } = cartFreebies;
			return updatedFreebies;
		}

		const reduceFreebieAmount = newFreebies.find((nf) => nf.freeAmount < cartFreebies[nf.fid]?.length);
		if (reduceFreebieAmount) {
			const updatedFreebies = { ...cartFreebies, [reduceFreebieAmount.fid]: cartFreebies[reduceFreebieAmount.fid]?.slice(0, reduceFreebieAmount.freeAmount) };
			return updatedFreebies;
		}
	}
}

function total_per_group(calculator, prods, group_pid, cart) {
	const groupProds = prods.reduce((acc, { par_pid, pid, price, conf }) => {
		const { volume_price } = conf ?? {};
		if (par_pid === group_pid) acc[pid] = { price, volume_price };
		return acc;
	}, {});
	const pricePerGroup = Object.entries(cart?.qty ?? {})
		.filter(([pid]) => groupProds[pid])
		.reduce((acc, [pid, q]) => calculator.calculateProductPrice({
			pid, 
			qty: q, 
			price: groupProds[pid].price, 
			volumePrice: groupProds[pid].volume_price,
			loyaltyPricing: groupProds[pid].loyalty_pricing,
		}).total.add?.(acc), 0)?.value;

	return pricePerGroup;
}

export function calc_delivery(calculator, cart) {
	const { cosm_prods = [] } = app.app_data.ws_data ?? {};	
	const cat_data = COSM_CATEGORIES(cosm_prods);

	const cart_prods = cosm_prods.filter((p) => cart?.qty?.[p.pid]);
	const oneProdPerGroup = Object.values(Object.fromEntries(cart_prods.reduce((acc, p) => (!p.conf?.has_free_delivery ? [...acc, [p.par_pid, p]] : acc), [])));

	return oneProdPerGroup.reduce((acc, p) => {
		const { has_free_delivery, free_delivery_from, delivery_price } = cat_data.find(({ pid }) => pid === p.par_pid)?.conf ?? {};
		if (has_free_delivery || (free_delivery_from && total_per_group(calculator, cart_prods, p.par_pid, cart) >= free_delivery_from)) return acc;
		return acc.add(delivery_price || DEFAULT_DELIVERY_PRICE);
	}, currency(0));
}

export function get_total(calculator, cart_prods, cart, discount) {
	const { cosm_prods = [] } = app.app_data.ws_data ?? {};

	const prods = cart_prods ?? CART_PRODS(cosm_prods, cart);

	let qty = 0;
	const total = prods.reduce((acc, { pid, price, conf }) => {
		const { volume_price, loyalty_pricing } = conf ?? {};
		const q = cart?.qty?.[pid] || 0;
		qty += q;

		const { total } = calculator.calculateProductPrice({
			pid, 
			qty: q, 
			price, 
			volumePrice: volume_price,
			loyaltyPricing: loyalty_pricing
		});
		return acc.add(total);
	}, currency(0));

	const deliveryFee = calc_delivery(calculator, cart);
	const discountTotal = (() => {
		try {
			return discount ? discount_engine.apply_cosm_discount(calculator, total, discount, prods, deliveryFee, cart?.qty) : 0;
		} catch (err) {
			return 0;
		}
	})();

	const subtotal = total.add(deliveryFee);
	const totalWithoutGst = subtotal.add(discountTotal);
	const gst = totalWithoutGst.multiply(0.1);
	const totalWithGst = totalWithoutGst.add(gst);

	return { total, deliveryFee, gst, totalWithoutGst, totalWithGst, qty, subtotal, discountTotal };
}

export async function fetch_prods(forceRefresh = false) {
	if (!forceRefresh && app.app_data.ws_data?.__cosm_prods_promise) {
		return await app.app_data.ws_data.__cosm_prods_promise;
	}

	const promise = prods_model.get_record(null, null, {
		act: 'all_prods', 
		group: 'cosm_prod'
	}).then(res => res?.path ?? []);

	promise.then(prods => {
		app.app_data.ws_data.cosm_prods = prods;

		// I don't know how long nurses navigate to and from the store, maybe this value could be lower
		const stale_time = app.settings.is_local
			? [10, 'seconds']
			: [1, 'hour'];

		const ms = moment.duration(...stale_time).asMilliseconds();

		setTimeout(() => {
			// Clear the cached value after the appropriate amount of time. Note we _cannot_ remove
			// `app.app_data.ws_data.cosm_prods` itself, because other parts of the code just expect it to be there
			// rather than attempting to load the data properly. But I'm not going to fix that. All of this should
			// be solved by something like... react query stale time.
			if (app.app_data.ws_data?.__cosm_prods_promise === promise) {
				// If the reference is different something else probably forced a refresh. Yes, we could keep track of
				// the timeout id and clear it if/when this happens, but whatever.
				delete app.app_data.ws_data?.__cosm_prods_promise;
			}
		}, ms);
	}).catch(_ => {
		delete app.app_data.ws_data?.__cosm_prods_promise;
	});

	app.app_data.ws_data = {
		...app.app_data.ws_data,
		// Store the inflight promise so repeated loads of the page (navigating back and forth?) don't trigger multiple
		// fetches. Unlikely edge case, but still, best to avoid.
		__cosm_prods_promise: promise,
	};

	return await promise;
}

export function failed_min_requirements(calculator, cart) {
	const prod_data = app.app_data.ws_data?.cosm_prods ?? [];
	const cat_data = COSM_CATEGORIES(prod_data);
	const { qty } = cart ?? {}; 
	for (const group of cat_data.filter((c) => c.conf)) {
		const { min_grp_price,  min : minimum_product_quantity  = 0, exclude = [] } = group.conf;

		const prodsForCategory = prod_data.filter(p=> p.par_pid == group.pid && !exclude.includes(p.pid) && qty[p.pid]);
		if(prodsForCategory.length > 0){
			const total = prodsForCategory.reduce((total,p)=> total + (qty?.[p.pid] || 0), 0);
			if(total && total < (minimum_product_quantity || 0)){
				return `Your order does not match the minimum product quantity requirements for ${group.name}. You must order at least ${minimum_product_quantity} product${minimum_product_quantity > 1 ? 's' : ''} from this category.`;
			}
			const hasAchievedMinimumSpend = !(Number(min_grp_price) > 0) || total_per_group(calculator, prod_data, group.pid, cart) >= min_grp_price;
			if (!hasAchievedMinimumSpend) {
				return `Your order does not match the minimum spend requirements for ${group.name}. You must spend at least $${min_grp_price} on products from this category.`;
			} 
		}
	}
}

export function save_cart(recordExist, { qty, comments, freebies, promotions } = {}) {
	const action = recordExist ? 'update_record' : 'set_record';
	return firebase_database[action](CART_LOC(), { ...(qty && { qty }), ...(comments && { comments }), ...(freebies && { freebies }), ...(promotions && { promotions }) }, { add_hist: true });
}

export function delete_cart() {
	delete app.localStorage.cosmDis;
	firebase_database.delete_record(CART_LOC());
}

export async function checkForCartChange({previousProducts, cart, products}) {
	let unavailableProducts = [];
	let errorMessage = '';

	const currentProducts = await fetch_prods(true);
	const currentActiveCartProds = CART_PRODS(currentProducts, cart);

	if (currentActiveCartProds.length < Object.keys(cart?.qty ?? {}).length) {
		await removeInactiveProducts(cart, currentActiveCartProds);
		unavailableProducts = previousProducts.current.filter(prod => !currentActiveCartProds.find(p => p.pid === prod.pid));
		errorMessage = `Some products are no longer available and have been removed from your cart${unavailableProducts.length > 0 ? ': ' + unavailableProducts.map(prod => prod.name).join(', ') : ''}`;
		previousProducts.current = currentActiveCartProds;
	}

	const productsWithDifPrices = verifyCartPrices(currentActiveCartProds, products);

	if (productsWithDifPrices.length > 0) {
		const parsedPriceDifferences = productsWithDifPrices
										.map(prod => `${prod.name} (was ${prod.oldPrice}, now ${prod.newPrice})`)
										.join(', ');
		errorMessage += `\n The following products have updated prices: ${parsedPriceDifferences}.`;
	}

	const hasErrorMessage = errorMessage.length > 0;

	if (hasErrorMessage) {
		return errorMessage;
	}

	return null;
} 

export function verifyCartPrices(currentActiveCartProds, previousProducts) {
	const priceChangedProducts = [];
	for (const prod of currentActiveCartProds) {
		const oldProduct = previousProducts.find(p => p.pid === prod.pid);

		if (oldProduct?.price !== prod?.price) {
			priceChangedProducts.push({
				name: oldProduct.name,
				oldPrice: oldProduct.price,
				newPrice: prod.price,
			});
		}
	}

	return priceChangedProducts;
}

export async function removeInactiveProducts(cart, products) {
    const cartProducts = Object.keys(cart?.qty ?? {});
    const activeProducts = products?.map(p => p?.pid);
    const toDelete = cartProducts.filter(pid => !activeProducts.includes(pid));

    if (toDelete.length === 0) {
        return;
    }

    toDelete.forEach(pid => {
        delete cart.qty[pid];
        if (cart.comments) {
            delete cart.comments[pid];
        }
    });

    const updatedFreebies = getUpdatedFreebies(products, cart.qty, cart.freebies || {});

    await save_cart(cart, {
        qty: cart.qty,
        comments: cart.comments,
        freebies: updatedFreebies
    });
}
